'use client';

/**
 * useSplitPanel Hook
 *
 * State management hook for SplitPanel component.
 * Provides open/close functionality, selected item state, and optional URL sync.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface UseSplitPanelUrlSync {
  /** Get current panel ID from URL */
  getPanelId: () => string | null;
  /** Set panel ID in URL (null to remove) */
  setPanelId: (id: string | null) => void;
}

export interface UseSplitPanelOptions<T> {
  /** Callback when panel opens */
  onOpen?: (item: T) => void;
  /** Callback when panel closes */
  onClose?: () => void;
  /** Initial selected item */
  initialItem?: T | null;
  /** ID field name for comparison and URL sync */
  idField?: keyof T;
  /** URL sync configuration - enables shareable panel URLs */
  urlSync?: UseSplitPanelUrlSync;
  /** Function to find item by ID (required for URL sync to restore state) */
  findItemById?: (id: string) => T | null | undefined;
}

export interface UseSplitPanelReturn<T> {
  /** Whether the panel is open */
  isOpen: boolean;
  /** Currently selected item */
  selectedItem: T | null;
  /** Selected item ID (useful when item not yet loaded) */
  selectedId: string | null;
  /** Open the panel with an item */
  openPanel: (item: T) => void;
  /** Open the panel with just an ID (item loaded separately) */
  openPanelById: (id: string) => void;
  /** Close the panel */
  closePanel: () => void;
  /** Toggle the panel with an item (opens if closed or different item, closes if same item) */
  togglePanel: (item: T) => void;
  /** Update the selected item without changing open state */
  updateSelectedItem: (item: T | null) => void;
  /** Props to spread on SplitPanel component */
  panelProps: {
    open: boolean;
    onClose: () => void;
  };
}

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook for managing SplitPanel state with optional URL sync.
 *
 * @example
 * ```tsx
 * // Basic usage (no URL sync)
 * const { isOpen, selectedItem, openPanel, closePanel, panelProps } = useSplitPanel<Business>({
 *   idField: 'id',
 * });
 *
 * // With URL sync (Next.js example)
 * const searchParams = useSearchParams();
 * const router = useRouter();
 * const pathname = usePathname();
 *
 * const { panelProps, selectedItem } = useSplitPanel<Business>({
 *   idField: 'id',
 *   urlSync: {
 *     getPanelId: () => searchParams.get('panelId'),
 *     setPanelId: (id) => {
 *       const params = new URLSearchParams(searchParams);
 *       if (id) {
 *         params.set('panelId', id);
 *       } else {
 *         params.delete('panelId');
 *       }
 *       router.replace(`${pathname}?${params.toString()}`, { scroll: false });
 *     },
 *   },
 *   findItemById: (id) => businesses.find(b => b.id === id),
 * });
 * ```
 */
export function useSplitPanel<T extends object>(options: UseSplitPanelOptions<T> = {}): UseSplitPanelReturn<T> {
  const { onOpen, onClose, initialItem = null, idField, urlSync, findItemById } = options;

  // Get initial state from URL if urlSync is provided, otherwise from initialItem.
  // Evaluated once on mount — the useState initializer guarantees this.
  const [isOpen, setIsOpen] = useState(() => {
    if (urlSync && idField && urlSync.getPanelId()) return true;
    return initialItem !== null;
  });
  const [selectedItem, setSelectedItem] = useState<T | null>(initialItem);
  const [selectedId, setSelectedId] = useState<string | null>(() => {
    if (urlSync && idField) {
      const urlId = urlSync.getPanelId();
      if (urlId) return urlId;
    }
    return initialItem && idField ? String(initialItem[idField]) : null;
  });

  // Sync URL to state on mount and URL changes
  useEffect(() => {
    if (!urlSync || !idField) return;

    const urlId = urlSync.getPanelId();
    if (urlId && urlId !== selectedId) {
      setSelectedId(urlId);
      setIsOpen(true);
      // Try to find the item if findItemById is provided
      if (findItemById) {
        const item = findItemById(urlId);
        if (item) {
          setSelectedItem(item);
        }
      }
    } else if (!urlId && isOpen) {
      // URL cleared, close panel
      setIsOpen(false);
      setSelectedId(null);
    }
    // Note: We intentionally only depend on getPanelId() result to avoid infinite loops
    // The effect should run when URL changes, not when other state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSync?.getPanelId(), idField]);

  // Update selected item when data loads/changes and we have a selectedId.
  // Uses ID-based comparison (not reference) to avoid infinite re-render loops
  // when findItemById returns new object references on each render.
  // Includes findItemById in deps so we re-run when the lookup closure
  // updates after a tRPC query commits new data — without that, a panel
  // restored from URL on mount stays empty until the user re-clicks the
  // row (the original useEffect was capturing the stale, pre-fetch
  // closure that returned undefined). The setSelectedItem callback is
  // ID-stable so adding the dep doesn't introduce a render loop: the
  // updater short-circuits when the same id is already selected.
  useEffect(() => {
    if (selectedId && findItemById) {
      const item = findItemById(selectedId);
      if (item) {
        const itemId = idField ? String(item[idField]) : null;
        // Only update if the item is for the correct ID (sanity check)
        // and we don't already have a selectedItem with the same ID
        setSelectedItem((prev) => {
          if (!prev) return item;
          const prevId = idField ? String(prev[idField]) : null;
          return prevId === itemId ? prev : item;
        });
      }
    }
  }, [selectedId, idField, findItemById]);

  const openPanel = useCallback(
    (item: T) => {
      const itemId = idField ? String(item[idField]) : null;
      setSelectedItem(item);
      setSelectedId(itemId);
      setIsOpen(true);

      // Sync to URL
      if (urlSync && itemId) {
        urlSync.setPanelId(itemId);
      }

      onOpen?.(item);
    },
    [idField, urlSync, onOpen],
  );

  const openPanelById = useCallback(
    (id: string) => {
      setSelectedId(id);
      setIsOpen(true);

      // Try to find and set the item
      if (findItemById) {
        const item = findItemById(id);
        if (item) {
          setSelectedItem(item);
          onOpen?.(item);
        }
      }

      // Sync to URL
      if (urlSync) {
        urlSync.setPanelId(id);
      }
    },
    [findItemById, urlSync, onOpen],
  );

  const closePanel = useCallback(() => {
    setIsOpen(false);

    // Sync to URL (clear panel param)
    if (urlSync) {
      urlSync.setPanelId(null);
    }

    onClose?.();
  }, [urlSync, onClose]);

  const togglePanel = useCallback(
    (item: T) => {
      if (isOpen && selectedItem && idField) {
        // If same item, close
        if (selectedItem[idField] === item[idField]) {
          closePanel();
          return;
        }
      }
      // Otherwise, open with new item
      openPanel(item);
    },
    [isOpen, selectedItem, idField, openPanel, closePanel],
  );

  const updateSelectedItem = useCallback(
    (item: T | null) => {
      setSelectedItem(item);
      if (item && idField) {
        setSelectedId(String(item[idField]));
      }
    },
    [idField],
  );

  const panelProps = useMemo(
    () => ({
      open: isOpen,
      onClose: closePanel,
    }),
    [isOpen, closePanel],
  );

  return {
    isOpen,
    selectedItem,
    selectedId,
    openPanel,
    openPanelById,
    closePanel,
    togglePanel,
    updateSelectedItem,
    panelProps,
  };
}

export default useSplitPanel;
